import { ElementType, useEffect, useRef, useState } from 'react';

import { Box, BoxProps, Divider, Stack, StackProps } from '@mui/material';
import { Types } from '@scorenco/core';

import {
  CompetitionRankingContext,
  useInitializeContext,
} from './CompetitionRankingContext';
import { CompetitionRankingHeader } from './CompetitionRankingHeader';
import { CompetitionRankingInfo } from './CompetitionRankingInfo';
import { CompetitionRankingInfoHeader } from './CompetitionRankingInfoHeader';
import {
  CompetitionRankingRow,
  CompetitionRankingRowProps,
} from './CompetitionRankingRow';

export type CompetitionRankingProps = BoxProps & {
  rankings?: Types.Competitions_Ranking_Detail[];
  highlightTeams?: number[];
  showHeader?: boolean;
  sport?: Types.Clubs_Sport;
  competitionId?: number;
  isClubhouse?: boolean;
  slots?: {
    row?: ElementType<CompetitionRankingRowProps>;
  };
  slotsProps?: {
    scrollContainer?: StackProps;
  };
};

export const CompetitionRanking = ({
  highlightTeams,
  showHeader = true,
  rankings,
  sport,
  competitionId,
  slots,
  slotsProps,
  isClubhouse = false,
  ...rest
}: CompetitionRankingProps) => {
  const [scrollStatus, setScrollStatus] = useState({
    showStart: false,
    showEnd: false,
  });
  const ref = useRef<HTMLDivElement>(null);
  const value = useInitializeContext({
    sport,
    highlightTeams,
    competitionId,
    isClubhouse,
  });
  const RowComponent = slots?.row || CompetitionRankingRow;

  useEffect(() => {
    const onScroll = () => {
      const {
        scrollWidth = 0,
        scrollLeft = 0,
        offsetWidth = 0,
      } = ref.current || {};
      setScrollStatus({
        showStart: scrollLeft > 0,
        showEnd: scrollLeft + offsetWidth < scrollWidth,
      });
    };
    onScroll();

    const node = ref.current;
    node?.addEventListener('scroll', onScroll);

    return () => {
      node?.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <CompetitionRankingContext.Provider value={value}>
      <Box
        sx={{
          position: 'relative',
          containerType: 'inline-size',
        }}
        {...rest}
      >
        <Stack divider={<Divider />}>
          {showHeader && <CompetitionRankingHeader />}

          {rankings?.map((ranking, index) => (
            <RowComponent key={index} ranking={ranking} />
          ))}
        </Stack>

        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            left: `${value.teamWidth + 1}rem`,
            [`@container (max-width: ${
              value.teamWidth + value.infoWidth + 1.5
            }rem)`]: {
              '&:before': {
                content: '" "',
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                width: '0.5rem',
                transition: 'background 0.6s ease-out',
                background: scrollStatus.showStart
                  ? 'linear-gradient(to left, rgba(16, 68, 127, 0), rgba(16, 68, 127, 0.15))'
                  : 'linear-gradient(to left, rgba(16, 68, 127, 0), rgba(16, 68, 127, 0.05))',
              },

              '&:after': {
                content: '" "',
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                width: '0.5rem',
                transition: 'background 0.6s ease-out',
                background: scrollStatus.showEnd
                  ? 'linear-gradient(to right, rgba(16, 68, 127, 0), rgba(16, 68, 127, 0.15))'
                  : 'linear-gradient(to right, rgba(16, 68, 127, 0), rgba(16, 68, 127, 0.05))',
              },
            },
          }}
        >
          <Stack
            {...slotsProps?.scrollContainer}
            ref={ref}
            sx={{
              pl: 1,
              overflowX: 'auto',
              justifyContent: 'space-between',
              ...slotsProps?.scrollContainer?.sx,
            }}
            direction="row"
          >
            <Box sx={{ flex: '0 0 0px' }} />
            <Stack
              sx={{ flex: `0 0 ${value.infoWidth}rem` }}
              divider={<Divider />}
            >
              {showHeader && <CompetitionRankingInfoHeader />}

              {rankings?.map((ranking, index) => (
                <CompetitionRankingInfo key={index} ranking={ranking} />
              ))}
            </Stack>
          </Stack>
        </Box>
      </Box>
    </CompetitionRankingContext.Provider>
  );
};
